import { useRef, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import Select from 'react-select'
import { IconHelp } from './CommonStylesTables'
import ReactTooltip from 'react-tooltip'

import {
  setInvitationSelected,
  setInvitationURL,
} from '../redux/invitationsReducer'
import { setNotificationState } from '../redux/notificationsReducer'
import { clearBulkIssuanceResult } from '../redux/bulkIssuanceResultReducer.js'

import FormInvitationEmail from './FormInvitationEmail'
import FormBulkCredentialsTemplate from './FormBulkCredentialsTemplate.js'
import FormDriversLicenseIssuance from './FormDriversLicenseIssuance'
import FormUniversalIssuance from './FormUniversalIssuance'
import FormPresentationQR from './FormPresentationQR'
import BulkIssuanceErrors from './BulkIssuanceErrors.js'

import PageHeader from './PageHeader.js'
import PageSection from './PageSection.js'

import { SubmitBtnModal } from './CommonStylesForms'

const HomeHeader = styled.h2`
  display: inline-block;
  margin-right: 10px;
  font-size: 1.3em;
`
const SelectContainer = styled.div`
  float: left;
  z-index: 100;
  width: 300px;
  margin-right: 15px;
  overflow: visible;
`
const Form = styled.form`
  overflow: hidden;
  margin-bottom: 10px;
`

function Home(props) {
  const dispatch = useDispatch()
  const settingsState = useSelector((state) => state.settings)
  const schemasState = useSelector((state) => state.schemas)
  const invitationsState = useSelector((state) => state.invitations)
  const contactsState = useSelector((state) => state.contacts)
  const bulkIssuanceResult = useSelector(
    (state) => state.bulkIssuanceResult.bulkIssuanceResult
  )

  const organizationName = settingsState.organizationName

  const submitBulkIssuanceBtn = useRef()

  const [credentialSchemasOptions, setCredentialSchemasOptions] = useState([])
  const [chosenCredential, setChosenCredential] = useState({})
  const [chosenBulkSchema, setChosenBulkSchema] = useState({})
  const [notReadyForIssuance, setNotReadyForIssuance] = useState(true)
  const [noBulkSchemaChosen, setNoBulkSchemaChosen] = useState(true)

  const [driversLicenseModalIsOpen, setDriversLicenseModalIsOpen] = useState(
    false
  )

  const [universalModalIsOpen, setUniversalModalIsOpen] = useState(false)

  const [connected, setConnected] = useState(false)
  const [waitingForConnection, setWaitingForConnection] = useState(false)
  const [waitingForVerification, setWaitingForVerification] = useState(false)
  const [verificationStatus, setVerificationStatus] = useState(undefined)
  const [
    verificationDefinitionOptions,
    setVerificationDefinitionOptions,
  ] = useState([])
  const [chosenSchema, setChosenSchema] = useState({})
  const [notReadyForVerification, setNotReadyForVerification] = useState(true)
  const [verificationButtonClicked, setVerificationButtonClicked] = useState(
    false
  )

  const [csvData, setCsvData] = useState([])

  const [emailModalIsOpen, setEmailModalIsOpen] = useState(false)
  const [bulkCredModalIsOpen, setBulkCredModalIsOpen] = useState(false)
  const [activeShemas, setActiveSchemas] = useState([])
  const [templateShema, setTemplateShema] = useState([])
  const closeBulkCredModal = () => {
    setNoBulkSchemaChosen(true)
    setBulkCredModalIsOpen(false)
    setChosenBulkSchema({})
  }
  const closeEmailModal = () => {
    setNotReadyForIssuance(true)
    setEmailModalIsOpen(false)
    setChosenCredential({})
  }

  const [verificationModalIsOpen, setVerificationModalIsOpen] = useState(false)

  const closeVerificationQRModal = () => setVerificationModalIsOpen(false)

  const closeDriversLicenseModal = () => {
    setNotReadyForIssuance(true)
    setDriversLicenseModalIsOpen(false)
    setChosenCredential({})
  }

  const closeUniversalModal = () => {
    setNotReadyForIssuance(true)
    setUniversalModalIsOpen(false)
    setChosenCredential({})
  }

  useEffect(() => {
    let options = []

    if (schemasState.schemas) {
      const activeShemas = schemasState.schemas.filter(
        (schema) => schema.active === true
      )
      setActiveSchemas(activeShemas)
      for (let i = 0; i < activeShemas.length; i++) {
        options.push({
          value: activeShemas[i].name,
          label: activeShemas[i].name.replaceAll('_', ' '),
        })
      }
      setCredentialSchemasOptions(options)
    }
  }, [schemasState.schemas])

  useEffect(() => {
    let options = []

    // (eldersonar) Handle governance options state (needs better description)
    if (schemasState.verificationDefinitions) {
      const activeShemas = schemasState.verificationDefinitions.filter(
        (schema) => schema.active === true || schema.active === undefined
      )
      for (let i = 0; i < activeShemas.length; i++) {
        options.push({
          id: i,
          label: activeShemas[i].verification_label,
          value: activeShemas[i].verification_label,
        })
      }
      setVerificationDefinitionOptions(options)
    }
  }, [schemasState.verificationDefinitions])

  useEffect(() => {
    if (csvData.length > 0) {
      submitBulkIssuanceBtn.current.removeAttribute('disabled') //(RomanStepanyan) Enable the button after the CSV file is uploaded
    } else {
      submitBulkIssuanceBtn.current.setAttribute('disabled', 'disabled')
    }
  }, [csvData])

  useEffect(() => {
    if (invitationsState.invitationURL !== '') {
      setWaitingForConnection(true)
    }
    if (contactsState.connection.state === 'active' && waitingForConnection) {
      setConnected(true)

      // (eldersonar) Trigger presentation request flow only if the invitation was created for verification
      if (verificationButtonClicked) {
        closeVerificationQRModal()
        requestPresentations()
      }
    }
  }, [
    invitationsState.invitationURL,
    contactsState.connection,
    waitingForConnection,
  ])

  useEffect(() => {
    if (props.verifiedCredential !== null && props.verifiedCredential !== '') {
      setVerificationStatus(true)
      setNotReadyForVerification(true)
    } else if (props.verifiedCredential === null) {
      setVerificationStatus(false)
      setNotReadyForVerification(true)
    } else {
      return
    }
  }, [props.verifiedCredential])

  function requestPresentations() {
    if (chosenSchema && chosenSchema.label) {
      const schema = schemasState.verificationDefinitions.find(
        ({ verification_label }) => verification_label === chosenSchema.label
      )

      // TODO: map connections and send to all connections
      props.sendRequest('PRESENTATIONS', 'ADMIN_REQUEST', {
        connection_id: contactsState.connection.connection_id,
        schema_id: schema.schema_id,
        attributes: schema.attribute_names,
        discovered_features: contactsState.connection.discovered_features,
        schema,
      })

      setChosenSchema({})
      setVerificationStatus(undefined)
      setWaitingForVerification(true)
      setVerificationButtonClicked(false)
      props.setVerifiedCredential('')
    } else {
      return
    }
  }

  const SchemaSelect = () => {
    return (
      <form>
        <HomeHeader>Verification Workflow</HomeHeader>
        <p>Select a Schema Option to Verify:</p>
        <SelectContainer>
          <Select
            id="chosenSchema"
            name="schemas"
            placeholder="---Choose Schema---"
            defaultValue={
              Object.keys(chosenSchema).length !== 0 ? chosenSchema : ''
            }
            onChange={(e) => handleSchemaChange(e)}
            menuPortalTarget={document.body}
            options={verificationDefinitionOptions}
          />
        </SelectContainer>

        <SubmitBtnModal
          disabled={notReadyForVerification}
          type="submit"
          onClick={(e) => {
            setVerificationButtonClicked(true)
            handleVerificationModalOpen(e)
          }}
          style={{ float: 'left' }}
        >
          Verify
        </SubmitBtnModal>
      </form>
    )
  }

  const CredentialSelect = () => {
    return (
      <form>
        <HomeHeader>Issuance Workflow</HomeHeader>
        <p>Select a Credential to Issue:</p>
        <SelectContainer>
          <Select
            className="react-select-container"
            name="chosenCred"
            placeholder="---Choose Credential---"
            defaultValue={
              Object.keys(chosenCredential).length !== 0 ? chosenCredential : ''
            }
            onChange={(e) => handleCredChange(e)}
            menuPortalTarget={document.body}
            options={credentialSchemasOptions}
            id="button-dropdown-credential-selection"
          />
        </SelectContainer>
        <SubmitBtnModal
          disabled={notReadyForIssuance}
          type="submit"
          onClick={(e) => {
            handleCredentialModalOpen(e)
          }}
          id="button-issue"
        >
          Issue
        </SubmitBtnModal>
      </form>
    )
  }

  const BulkCredentialSelect = () => {
    return (
      <form>
        <p>Select a schema to use to create a CSV template file:</p>
        <SelectContainer>
          <Select
            className="react-select-container"
            name="chosenCred"
            placeholder="---Choose Credential---"
            defaultValue={
              Object.keys(chosenBulkSchema).length !== 0 ? chosenBulkSchema : ''
            }
            onChange={(e) => handleBulkCredChange(e)}
            menuPortalTarget={document.body}
            options={credentialSchemasOptions}
            id="button-dropdown-credential-selection"
          />
        </SelectContainer>
        <SubmitBtnModal
          disabled={noBulkSchemaChosen}
          type="submit"
          onClick={(e) => {
            handleBulkCredModalOpen(e)
          }}
          id="button-issue"
        >
          Generate CSV Template
        </SubmitBtnModal>
      </form>
    )
  }

  const onBtnClick = (e) => {
    if (submitBulkIssuanceBtn.current) {
      submitBulkIssuanceBtn.current.setAttribute('disabled', 'disabled')
    }
  }

  function handleCredChange(e) {
    setChosenCredential(e)
    setNotReadyForIssuance(false)
  }

  function handleBulkCredChange(e) {
    const schemaForTemplate = activeShemas.map((schema) => {
      if (schema.name === e.value) {
        return schema
      }
    })
    setTemplateShema(schemaForTemplate)
    setChosenBulkSchema(e)
    setNoBulkSchemaChosen(false)
  }

  function handleSchemaChange(e) {
    setChosenSchema(e)
    setNotReadyForVerification(false)
  }

  function handleCredentialModalOpen(e) {
    e.preventDefault()
    console.log(chosenCredential.value)
    if (chosenCredential.value === 'Email') {
      setEmailModalIsOpen((o) => !o)
    } else if (chosenCredential.value === 'Drivers_License') {
      // (AmmonBurgi) We need to reset the invitation so the modal doesn't proceed before a new invitation is received.
      dispatch(setInvitationSelected({}))
      dispatch(setInvitationURL(''))

      setDriversLicenseModalIsOpen((o) => !o)

      props.sendRequest('OUT_OF_BAND', 'CREATE_INVITATION', {
        handshakeProtocol: 'https://didcomm.org/didexchange/1.1',
        alias: 'Invitation',
        invitationMode: 'once',
        accept: 'auto',
        public: false,
        invitationStatus: 'active',
        invitationDescription:
          "Invitation for Driver's License credential issuance.",
        invitationLabel: settingsState.organizationName,
        invitationRole: 'Holder',
        usesAllowed: 1,
      })
    } else {
      // (AmmonBurgi) We need to reset the invitation so the modal doesn't proceed before a new invitation is received.
      dispatch(setInvitationSelected({}))
      dispatch(setInvitationURL(''))

      setUniversalModalIsOpen((o) => !o)

      props.sendRequest('OUT_OF_BAND', 'CREATE_INVITATION', {
        handshakeProtocol: 'https://didcomm.org/didexchange/1.1',
        alias: 'Invitation',
        invitationMode: 'once',
        accept: 'auto',
        public: false,
        invitationStatus: 'active',
        invitationDescription: 'Invitation for Universal credential issuance.',
        invitationLabel: settingsState.organizationName,
        invitationRole: 'Holder',
        usesAllowed: 1,
      })
    }
  }

  function handleVerificationModalOpen(e) {
    e.preventDefault()
    // (AmmonBurgi) We need to reset the invitation so the modal doesn't proceed before a new invitation is received.
    dispatch(setInvitationSelected({}))
    dispatch(setInvitationURL(''))

    setVerificationModalIsOpen((o) => !o)

    props.sendRequest('OUT_OF_BAND', 'CREATE_INVITATION', {
      handshakeProtocol: 'https://didcomm.org/didexchange/1.1',
      alias: 'Invitation',
      invitationMode: 'once',
      accept: 'auto',
      public: false,
      invitationStatus: 'active',
      invitationDescription: 'Invitation for credential verification',
      invitationLabel: settingsState.organizationName,
      invitationRole: 'Holder',
      usesAllowed: 1,
    })
  }

  function handleBulkCredModalOpen(e) {
    e.preventDefault()
    if (chosenBulkSchema.value) {
      setBulkCredModalIsOpen((o) => !o)
    }
  }

  //(RomanStepanyan) Converting CSV file to an array of objects
  function csvToArray(str, delimiter = ',', fileName) {
    const rows = str.split(/(?:\r\n|\n)+/).filter((el) => {
      return el.length !== 0
    })
    const headers = rows.splice(0, 1)[0].split(',')

    // They did not set the header correctly
    if (!headers.includes('email')) {
      dispatch(
        setNotificationState({
          message: `Please include an "email" header in your CSV!`,
          type: 'warning',
        })
      )
      return false
    }

    const arr = rows.map(function (row) {
      //Splitting and removing white spaces
      const values = row.split(delimiter)
      //Assemble the object
      //(RomanStepanyan) Create the object with the first three headers as key-value pairs
      const el = headers.slice(0, 3).reduce((object, header, index) => {
        object[header] = values[index]
        return object
      }, {})
      //(RomanStepanyan) Add the remaining headers and values to the attributes array
      el.attributes = headers.slice(3).map((header, index) => {
        return {
          name: header,
          value: values[index + 3],
        }
      })
      return el
    })
    setCsvData(arr)
    // Reset Bulk emails to original state (remove any error messages)
    clearBulkIssuanceResult()

    // The Csv file successfully imported
    dispatch(
      setNotificationState({
        message: `Your CSV file, ${fileName}, has been imported. Next, click on "Issue Credentials".`,
        type: 'notice',
      })
    )
  }

  //(RomanStepanyan) CSV file upload
  let csvFileSelectHandler = (event) => {
    event.preventDefault()
    const file = event.target.files[0]

    // The File Exists
    if (file) {
      // Read in the csv
      const reader = new FileReader()
      reader.onload = function (e) {
        csvToArray(reader.result, ',', file.name)
      }
      reader.readAsText(file)
    } else {
      setCsvData([])
    }
  }

  const handleBulkIssuanceSubmit = async (e) => {
    e.preventDefault()

    // (eldersonar) Check for the public DID
    if (schemasState.issuerDID) {
      onBtnClick()
      if (csvData) {
        props.sendRequest('BULK_CREDENTIALS', 'ISSUE', {
          emails: csvData,
        })
      } else {
        dispatch(
          setNotificationState({
            message:
              'Bulk credential issuance failed: No CSV file has been selected.',
            type: 'error',
          })
        )
      }
    } else {
      dispatch(
        setNotificationState({
          message: "Error: Public DID is not set. Invitations can't be sent.",
          type: 'error',
        })
      )
    }

    //Unselect the file
    document.getElementById('bulkIssuanceFileInputForm').reset()
    //Redisable File
    submitBulkIssuanceBtn.current.setAttribute('disabled', 'disabled')
  }

  return (
    <>
      <PageHeader title={organizationName} />
      <PageSection>
        <CredentialSelect />
      </PageSection>
      <PageSection>
        {connected ? (
          waitingForVerification ? (
            verificationStatus !== undefined ? (
              verificationStatus ? (
                props.verifiedCredential ? (
                  <>
                    <SchemaSelect />
                  </>
                ) : (
                  <>
                    <SchemaSelect />
                  </>
                )
              ) : (
                <>
                  <p>
                    There was a problem verifying your credential. Please try
                    again or contact support by clicking on the link below:
                  </p>
                  <SchemaSelect />
                </>
              )
            ) : (
              <>
                <p>
                  You will now receive a request on your mobile app to send your
                  credential to us for verification.
                </p>
              </>
            )
          ) : (
            <SchemaSelect />
          )
        ) : (
          <SchemaSelect />
        )}
      </PageSection>
      <PageSection>
        <HomeHeader>Bulk Credential Issuance</HomeHeader>
        <IconHelp
          data-tip
          data-for="logoTip"
          data-delay-hide="250"
          data-multiline="true"
          alt="Help"
        />
        <ReactTooltip
          id="logoTip"
          effect="solid"
          type="info"
          backgroundColor={useTheme().primary_color}
        >
          <span>
            This flow allows generating CSV files based on desired schema.
            <br />
            Time to accept the credential value is required.
          </span>
        </ReactTooltip>
        <p>
          Proven allows you to issue credentials in bulk by sending a unique
          invitation to each recipient via email.
        </p>
        <BulkCredentialSelect />
        <p>
          After you have populated your CSV file with data, click "Choose File",
          select your file, and click "Issue Credentials"
        </p>
        <Form
          onSubmit={handleBulkIssuanceSubmit}
          id="bulkIssuanceFileInputForm"
        >
          <input
            type="file"
            accept=".csv"
            onChange={csvFileSelectHandler}
            id="input-bulk-credentials"
          />
          <button
            type="submit"
            ref={submitBulkIssuanceBtn}
            disabled
            id="button-send-emails"
          >
            Issue Credentials
          </button>
        </Form>
        <BulkIssuanceErrors bulkIssuanceResult={bulkIssuanceResult} />
      </PageSection>
      <FormInvitationEmail
        sendRequest={props.sendRequest}
        emailModalIsOpen={emailModalIsOpen}
        closeEmailModal={closeEmailModal}
      />
      <FormBulkCredentialsTemplate
        bulkCredModalIsOpen={bulkCredModalIsOpen}
        closeBulkCredModal={closeBulkCredModal}
        templateShema={templateShema}
      />
      <FormDriversLicenseIssuance
        driversLicenseModalIsOpen={driversLicenseModalIsOpen}
        closeDriversLicenseModal={closeDriversLicenseModal}
        sendRequest={props.sendRequest}
      />
      <FormPresentationQR
        verificationModalIsOpen={verificationModalIsOpen}
        closeVerificationQRModal={closeVerificationQRModal}
        sendRequest={props.sendRequest}
      />
      <FormUniversalIssuance
        universalModalIsOpen={universalModalIsOpen}
        closeUniversalModal={closeUniversalModal}
        chosenCredential={chosenCredential.value}
        sendRequest={props.sendRequest}
      />
    </>
  )
}

export default Home
